import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { ButtonType } from 'app/shared/buttons';
import { ModalContentItem, ModalService } from 'app/shared/modal';

@Injectable()
export class UnsavedChangesPromptService {
    private modalService = inject(ModalService);
    private router = inject(Router);

    private routerSubscription: Subscription | null = null;
    // Manual Subscription tracking is intentional here (not a takeUntilDestroyed
    // candidate): this is a root-scoped service whose router subscription is
    // started/stopped dynamically as components register/unregister as listeners,
    // not tied to any single component's destruction.
    private listeners: { id: string; isDirty: () => boolean }[] = [];
    private subscriptions: Set<string> = new Set();
    private formsArray: (() => boolean)[] = [];

    /**
     * Starts listening for unsaved form changes in the specified component.
     * @param componentId - The ID of the component. If a parent component has multiple children mounted on the same URL,
     * the ID should be the same for all children (referencing the parent component).
     *
     * @param newFormsArray - An array of functions that determine if a form is dirty. e.g. [() => this.form.dirty]
     */
    startListeningForUnsavedChanges(componentId: string, newFormsArray: (() => boolean)[]): void {
        this.formsArray = [...new Set([...this.formsArray, ...newFormsArray])];

        newFormsArray.forEach((isDirty) => {
            this.listeners.push({ id: componentId, isDirty });
        });

        if (!this.subscriptions.has(componentId)) {
            this.subscriptions.add(componentId);

            if (!this.routerSubscription) {
                // No error handler needed: router.events is an internal Router stream,
                // not a network call, and does not error.
                this.routerSubscription = this.router.events.subscribe((event) => {
                    if (event instanceof NavigationStart) {
                        if (this.isAnyFormDirty()) {
                            this.generatePrompt(componentId, () => {
                                this.router.navigateByUrl(event.url);
                            });
                            // Prevent navigation until the user makes a choice
                            this.router.navigateByUrl(this.router.url);
                        }
                    }
                });

                window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
            }
        }
    }

    private isAnyFormDirty(): boolean {
        return this.listeners.some((listener) => listener.isDirty());
    }

    handleBeforeUnload(event: any): void {
        if (this.isAnyFormDirty()) {
            event.preventDefault();
            event.returnValue = ''; // Show default browser prompt
        }
    }

    stopListeningForUnsavedChanges(componentId: string): void {
        this.listeners = this.listeners.filter((listener) => listener.id !== componentId);
        this.subscriptions.delete(componentId);

        this.formsArray = this.formsArray.filter((form, index) => {
            return !this.listeners.some((listener) => listener.isDirty === form && listener.id === componentId);
        });

        if (this.listeners.length === 0) {
            if (this.routerSubscription) {
                this.routerSubscription.unsubscribe();
                this.routerSubscription = null;
            }

            window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
        }
    }

    generatePrompt(componentId: string, onLeaveConfirmed: () => void, onStayConfirmed?: () => void): void {
        const contentItems: ModalContentItem[] = [
            {
                ngClass: 'relax h2',
                contentTranslationKey: 'unsaved_changes_prompt.title',
            },
            {
                ngClass: 'relax body-14',
                contentTranslationKey: 'unsaved_changes_prompt.description',
            },
        ];

        // Stop listening for unsaved changes to allow navigation
        this.stopListeningForUnsavedChanges(componentId);

        this.modalService.createModal({
            size: 'small',
            contentItems,
            onClose: () => {
                this.startListeningForUnsavedChanges(componentId, this.formsArray);
            },
            buttons: [
                {
                    label: 'unsaved_changes_prompt.leave_without_saving',
                    type: ButtonType.text,
                    callback: () => {
                        this.modalService.closeModal();
                        onLeaveConfirmed();
                    },
                },
                {
                    label: 'unsaved_changes_prompt.save_changes',
                    callback: () => {
                        this.modalService.closeModal();
                        this.startListeningForUnsavedChanges(componentId, this.formsArray);
                        if (onStayConfirmed) {
                            onStayConfirmed();
                        }
                    },
                },
            ],
        });
    }
}
